import { useEffect, useRef, useState } from "react";

/**
 * Returns a throttled version of the input value that updates at most once
 * per `intervalMs` milliseconds. Unlike debounce, throttle provides regular
 * updates during continuous changes rather than waiting for activity to stop.
 *
 * Useful for high-frequency updates like video currentTime where you want
 * periodic updates without overwhelming downstream computations.
 */
export const useThrottledValue = <T>(value: T, intervalMs: number): T => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastUpdateRef = useRef(0);
  const pendingValueRef = useRef(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    pendingValueRef.current = value;
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;

    if (timeSinceLastUpdate >= intervalMs) {
      lastUpdateRef.current = now;
      setThrottledValue(value);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    } else if (!timeoutRef.current) {
      const remainingTime = intervalMs - timeSinceLastUpdate;
      timeoutRef.current = setTimeout(() => {
        lastUpdateRef.current = Date.now();
        setThrottledValue(pendingValueRef.current);
        timeoutRef.current = null;
      }, remainingTime);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, intervalMs]);

  return throttledValue;
};
